import { formatDurationSeconds, parseDurationSeconds } from "./duration";
import type { ParameterValue } from "./value.model";

type Declaration<K extends ParameterValue["kind"]> = {
  [P in keyof Extract<ParameterValue, { kind: K }>]: Extract<ParameterValue, { kind: K }>[P];
};

export type QuantityDeclaration = Declaration<"Quantity">;
export type IntegerDeclaration = Declaration<"Integer">;
export type QuantityDisplay = NonNullable<QuantityDeclaration["display"]>;

/** How a number is typed and read: `input` picks the control, `suffix` is the unit shown beside it. */
export interface QuantityText {
  input: QuantityDisplay | "number";
  format(value: number): string;
  parse(text: string): number | undefined;
  suffix?: string;
}

export interface QuantityTextDeclaration {
  kind: string;
  display?: QuantityDisplay;
  unit?: { display: string };
}

const DURATION: QuantityText = { input: "duration", format: formatDurationSeconds, parse: parseDurationSeconds };

function parseNumber(text: string): number | undefined {
  if (!text.trim()) return undefined;
  const value = Number(text);
  return Number.isFinite(value) ? value : undefined;
}

/** The suffix is the declaration's unit, unless `answer` is given: an answer shows the unit it carries, or none. */
export function quantityText(
  declaration: QuantityTextDeclaration | undefined,
  answer?: { unit?: { display: string } },
): QuantityText {
  if (declaration?.kind === "Quantity" && declaration.display === "duration") return DURATION;
  const unit = answer ? answer.unit : declaration?.unit;
  return { input: "number", format: String, parse: parseNumber, ...(unit ? { suffix: unit.display } : {}) };
}
